import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, BookOpen, Lock, Upload, Loader2, Trash2, X, ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Lean Management", "Industrie 4.0", "Excellence opérationnelle", "Productivité", "Digitalisation industrielle", "Études de cas"];

function BookCover({ book }) {
  const [err, setErr] = useState(false);
  if (book.has_cover && !err) {
    return <img src={`${API}/books/${book.id}/cover`} alt={book.title} onError={() => setErr(true)} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full bg-ink-900 grain flex flex-col items-center justify-center p-6 text-center">
      <BookOpen size={32} className="text-gold relative z-10" />
      <span className="relative z-10 font-display text-paper text-lg font-semibold mt-4 leading-tight line-clamp-3">{book.title}</span>
    </div>
  );
}

export default function Books() {
  const [books, setBooks] = useState([]);
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("nlc_admin_key") || "");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [form, setForm] = useState({ title: "", author: "Majdi ZAROUGUI", description: "", category: CATEGORIES[0], members_only: false, buy_url: "", price: "", book: null, cover: null });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/books`);
      setBooks(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadBook = async (b) => {
    setDownloading(b.id);
    try {
      const res = await axios.get(`${API}/books/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${b.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement impossible.");
    } finally {
      setDownloading(null);
    }
  };

  const verify = async () => {
    if (!adminKey) return toast.error("Entrez votre clé créateur.");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-key": adminKey } });
      localStorage.setItem("nlc_admin_key", adminKey);
      setIsAdmin(true);
      toast.success("Accès créateur activé.");
    } catch (e) {
      toast.error("Clé créateur invalide.");
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!form.title || !form.author) return toast.error("Titre et auteur sont requis.");
    if (!form.book && !form.buy_url.trim()) return toast.error("Ajoutez le fichier du livre ou un lien d'achat externe.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("author", form.author);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("members_only", form.members_only ? "true" : "false");
      fd.append("buy_url", form.buy_url.trim());
      if (form.price) fd.append("price", form.price);
      if (form.book) fd.append("book", form.book);
      if (form.cover) fd.append("cover", form.cover);
      await axios.post(`${API}/books`, fd, { headers: { "x-admin-key": adminKey, "Content-Type": "multipart/form-data" } });
      toast.success("Livre publié !");
      setForm({ title: "", author: "Majdi ZAROUGUI", description: "", category: CATEGORIES[0], members_only: false, buy_url: "", price: "", book: null, cover: null });
      setShowCreator(false);
      load();
    } catch (e) {
      toast.error("Échec de la publication.", { description: e?.response?.data?.detail || "Réessayez." });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/books/${id}`, { headers: { "x-admin-key": adminKey } });
      toast.success("Livre retiré.");
      load();
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="[ Bibliothèque ]"
        titleLines={["Les livres de", "Nova Lean."]}
        intro="Ouvrages et guides pratiques sur le Lean Management, l'Industrie 4.0 et l'excellence opérationnelle, signés par nos experts."
      />

      <section className="bg-paper py-16 md:py-24" data-testid="books-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-center justify-between gap-6 mb-12">
            <Reveal>
              <span className="label-mono text-gold">[ {books.length} ouvrage{books.length > 1 ? "s" : ""} ]</span>
            </Reveal>
            <Reveal delay={0.1}>
              <button
                onClick={() => setShowCreator(true)}
                data-testid="open-creator-space"
                className="group inline-flex items-center gap-2 rounded-full border hairline text-ink px-5 py-2.5 label-mono hover:border-gold hover:text-gold transition-colors"
              >
                <Lock size={13} /> Espace créateur
              </button>
            </Reveal>
          </div>

          {books.length === 0 ? (
            <Reveal>
              <div className="text-center py-24 border hairline rounded-sm bg-white">
                <BookOpen size={40} className="text-ink/30 mx-auto" />
                <p className="font-display text-2xl mt-6 text-ink/70">Aucun livre publié pour le moment.</p>
                <p className="text-ink/45 mt-2">Revenez bientôt, la bibliothèque s'enrichit.</p>
              </div>
            </Reveal>
          ) : (
            <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {books.map((b) => (
                <StaggerItem key={b.id}>
                  <article className="group h-full flex flex-col rounded-sm overflow-hidden border hairline bg-white hover:shadow-[0_20px_50px_-20px_rgba(10,26,51,0.3)] transition-shadow duration-500" data-testid={`book-${b.id}`}>
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <div className="w-full h-full group-hover:scale-105 transition-transform duration-[900ms] ease-out">
                        <BookCover book={b} />
                      </div>
                      <span className="absolute top-4 left-4 label-mono text-paper bg-ink-900/70 backdrop-blur px-3 py-1.5 rounded-full">{b.category}</span>
                      {isAdmin && (
                        <button onClick={() => remove(b.id)} data-testid={`delete-book-${b.id}`} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-paper/90 text-ink flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors" title="Retirer">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-6">
                      <h3 className="font-display text-2xl font-semibold leading-tight">{b.title}</h3>
                      <p className="label-mono text-gold mt-2">{b.author}</p>
                      {b.description && <p className="text-ink/55 text-sm mt-3 flex-1 leading-relaxed line-clamp-3">{b.description}</p>}
                      {b.buy_url && (
                        <a href={b.buy_url} target="_blank" rel="noopener noreferrer" data-testid={`buy-book-${b.id}`}
                          className="group/buy inline-flex items-center justify-between w-full mt-6 pt-5 border-t hairline label-mono text-gold hover:text-ink transition-colors">
                          Acheter le livre{b.price ? ` — ${b.price.toFixed(2).replace(".", ",")} €` : ""}
                          <ArrowUpRight size={16} className="group-hover/buy:rotate-45 transition-transform" />
                        </a>
                      )}
                      {b.has_file && (
                        <button
                          onClick={() => downloadBook(b)}
                          disabled={downloading === b.id}
                          data-testid={`download-book-${b.id}`}
                          className={`group/btn inline-flex items-center justify-between w-full label-mono text-ink hover:text-gold transition-colors disabled:opacity-60 ${b.buy_url ? "mt-2 pt-4" : "mt-6 pt-5 border-t hairline"}`}
                        >
                          {downloading === b.id ? "Téléchargement…" : "Télécharger le livre"}
                          {downloading === b.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} className="group-hover/btn:translate-y-0.5 transition-transform" />}
                        </button>
                      )}
                    </div>
                  </article>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>

      <AnimatePresence>
        {showCreator && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowCreator(false)}
            data-testid="creator-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-paper rounded-md p-8 max-h-[90vh] overflow-y-auto"
            >
              <button onClick={() => setShowCreator(false)} className="absolute top-5 right-5 text-ink/50 hover:text-ink" aria-label="Fermer"><X size={22} /></button>
              <span className="label-mono text-gold">Espace créateur</span>
              <h2 className="font-display text-3xl font-semibold mt-2">Publier un livre</h2>

              {!isAdmin ? (
                <div className="mt-8">
                  <label className="label-mono text-ink/60 block mb-2">Clé créateur</label>
                  <div className="flex gap-2">
                    <input
                      type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)}
                      data-testid="admin-key-input" placeholder="Votre clé secrète"
                      className="flex-1 bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold transition-colors"
                    />
                    <button onClick={verify} data-testid="admin-verify-btn" className="rounded-sm bg-ink text-paper px-5 label-mono hover:bg-gold hover:text-ink transition-colors">Valider</button>
                  </div>
                  <p className="text-ink/45 text-sm mt-3">Seul le créateur du site peut publier des livres.</p>
                </div>
              ) : (
                <form onSubmit={upload} className="mt-8 space-y-4" data-testid="upload-form">
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Titre *</label>
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="book-title" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label-mono text-ink/60 block mb-2">Auteur *</label>
                      <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="book-author" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold" />
                    </div>
                    <div>
                      <label className="label-mono text-ink/60 block mb-2">Catégorie</label>
                      <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="book-category" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold">
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Description</label>
                    <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="book-description" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold resize-none" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label-mono text-ink/60 block mb-2">Lien d'achat externe (ex. Coollibri)</label>
                      <input value={form.buy_url} onChange={(e) => setForm({ ...form, buy_url: e.target.value })} placeholder="https://…" data-testid="book-buy-url" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold" />
                    </div>
                    <div>
                      <label className="label-mono text-ink/60 block mb-2">Prix affiché (€)</label>
                      <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="29.90" data-testid="book-price" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold" />
                    </div>
                  </div>
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Fichier du livre (PDF/EPUB — optionnel si lien d'achat)</label>
                    <input type="file" accept=".pdf,.epub" onChange={(e) => setForm({ ...form, book: e.target.files[0] })} data-testid="book-file" className="w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-ink file:text-paper file:px-4 file:py-2 file:label-mono" />
                  </div>
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Couverture (image, optionnel)</label>
                    <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, cover: e.target.files[0] })} data-testid="cover-file" className="w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-ink file:text-paper file:px-4 file:py-2 file:label-mono" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer select-none py-1" data-testid="members-only-label">
                    <input type="checkbox" checked={form.members_only} onChange={(e) => setForm({ ...form, members_only: e.target.checked })} data-testid="members-only-checkbox" className="w-5 h-5 accent-[#C8992F]" />
                    <span className="flex items-center gap-1.5 text-ink/80"><Lock size={14} className="text-gold" /> Réserver ce livre aux membres connectés</span>
                  </label>
                  <button type="submit" disabled={uploading} data-testid="publish-book-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-60">
                    {uploading ? <><Loader2 size={16} className="animate-spin" /> Publication...</> : <><Upload size={15} /> Publier le livre</>}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
